import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import './AdminManagement.css';

const AdminResearch = () => {
    const [research, setResearch] = useState({
        areas: [],
        publications: [],
        collaborations: ''
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('area'); // 'area' or 'publication'
    const [editIndex, setEditIndex] = useState(null);
    const [formData, setFormData] = useState({});
    const token = localStorage.getItem('adminToken');

    const fetchResearch = React.useCallback(async () => {
        try {
            const response = await axios.get('/api/research');
            setResearch(response.data || { areas: [], publications: [], collaborations: '' });
        } catch (err) {
            console.error('Fetch error:', err);
            setError('Failed to load research data.');
        } finally {
            setLoading(false);
        }
    }, []);

    const openAddModal = (type) => {
        setModalType(type);
        setEditIndex(null);
        if (type === 'area') {
            setFormData({ title: '', description: '', longDescription: '', tech: '', link: '', github: '', icon: '🔬' });
        } else {
            setFormData({ title: '', authors: '', venue: '', year: new Date().getFullYear(), abstract: '', link: '' });
        }
        setShowModal(true);
    };

    const openEditModal = React.useCallback((type, index) => {
        setModalType(type);
        setEditIndex(index);
        if (type === 'area') {
            setFormData({ ...research.areas[index] });
        } else {
            setFormData({ ...research.publications[index] });
        }
        setShowModal(true);
    }, [research.areas, research.publications]);

    const handleDelete = React.useCallback(async (type, index) => {
        if (!window.confirm(`Delete this ${type}?`)) return;
        setError('');
        setSuccess('');
        try {
            let updatedResearch = { ...research };

            if (type === 'area') {
                updatedResearch.areas.splice(index, 1);
                await axios.put('/api/research', { areas: updatedResearch.areas }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                updatedResearch.publications.splice(index, 1);
                await axios.put('/api/research', { publications: updatedResearch.publications }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            setResearch(updatedResearch);
            setSuccess(`${type === 'area' ? 'Research Area' : 'Publication'} deleted successfully!`);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Delete failed.');
        }
    }, [research, token]);

    useEffect(() => {
        fetchResearch();
    }, [fetchResearch]);

    const closeModal = () => {
        setShowModal(false);
        setFormData({});
        setEditIndex(null);
    };

    const handleModalSave = async () => {
        setError('');
        setSuccess('');
        try {
            let updatedResearch = { ...research };

            if (modalType === 'area') {
                if (editIndex !== null) {
                    updatedResearch.areas[editIndex] = formData;
                } else {
                    updatedResearch.areas.push(formData);
                }
                await axios.put('/api/research', { areas: updatedResearch.areas }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                if (editIndex !== null) {
                    updatedResearch.publications[editIndex] = formData;
                } else {
                    updatedResearch.publications.push(formData);
                }
                await axios.put('/api/research', { publications: updatedResearch.publications }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            setResearch(updatedResearch);
            setSuccess(`${modalType === 'area' ? 'Research Area' : 'Publication'} ${editIndex !== null ? 'updated' : 'added'} successfully!`);
            closeModal();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed.');
        }
    };

    const saveCollaborations = async () => {
        setError('');
        setSuccess('');
        try {
            await axios.put('/api/research', { collaborations: research.collaborations }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccess('Collaborations updated successfully!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Update failed.');
        }
    };

    if (loading) return <div className="admin-manage-page">Loading...</div>;

    return (
        <div className="admin-manage-page">
            <header className="manage-header">
                <h1>Research & Innovation</h1>
                <p>Manage research areas, publications, and collaborations</p>
            </header>

            {error && <div className="login-error" style={{ marginBottom: '1.5rem' }}>{error}</div>}
            {success && <div className="success-message" style={{ marginBottom: '1.5rem' }}>{success}</div>}

            {/* Research Areas */}
            <div className="manage-section" style={{ marginBottom: '3rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h2 style={{ fontSize: '1.5rem', color: 'rgba(255,255,255,0.9)' }}>Research Areas</h2>
                    <button onClick={() => openAddModal('area')} className="save-btn">+ Add Area</button>
                </div>
                <div className="items-scroll-list">
                    <AnimatePresence>
                        {research.areas.map((area, index) => (
                            <motion.div
                                key={index}
                                className="item-row-card"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                            >
                                <div className="item-main">
                                    <h4>{area.icon} {area.title}</h4>
                                    <p>{area.description?.substring(0, 80)}...</p>
                                </div>
                                <div className="item-actions">
                                    <button onClick={() => openEditModal('area', index)} className="edit-btn">Edit</button>
                                    <button onClick={() => handleDelete('area', index)} className="delete-btn">Delete</button>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                    {research.areas.length === 0 && <p style={{ textAlign: 'center', opacity: 0.5 }}>No research areas yet.</p>}
                </div>
            </div>

            {/* Publications */}
            <div className="manage-section" style={{ marginBottom: '3rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h2 style={{ fontSize: '1.5rem', color: 'rgba(255,255,255,0.9)' }}>Publications</h2>
                    <button onClick={() => openAddModal('publication')} className="save-btn">+ Add Publication</button>
                </div>
                <div className="items-scroll-list">
                    <AnimatePresence>
                        {research.publications.map((pub, index) => (
                            <motion.div
                                key={index}
                                className="item-row-card"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                            >
                                <div className="item-main">
                                    <h4>{pub.title}</h4>
                                    <p>{pub.authors} • {pub.venue} ({pub.year})</p>
                                </div>
                                <div className="item-actions">
                                    <button onClick={() => openEditModal('publication', index)} className="edit-btn">Edit</button>
                                    <button onClick={() => handleDelete('publication', index)} className="delete-btn">Delete</button>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                    {research.publications.length === 0 && <p style={{ textAlign: 'center', opacity: 0.5 }}>No publications yet.</p>}
                </div>
            </div>

            {/* Collaborations */}
            <div className="manage-form-card" style={{ maxWidth: '800px', margin: '0 auto' }}>
                <h3>Collaborations Highlight</h3>
                <textarea
                    value={research.collaborations}
                    onChange={(e) => setResearch({ ...research, collaborations: e.target.value })}
                    style={{ height: '120px', width: '100%', marginBottom: '1rem' }}
                    placeholder="Highlight key partnerships with universities and industry leaders..."
                />
                <button onClick={saveCollaborations} className="save-btn" style={{ width: '100%' }}>Save Collaborations</button>
            </div>

            {/* Modal */}
            {ReactDOM.createPortal(
                <AnimatePresence>
                    {showModal && (
                        <div className="modal-overlay" onClick={closeModal}>
                            <motion.div
                                className="modal-content"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                onClick={(e) => e.stopPropagation()}
                                style={{ maxWidth: '700px' }}
                            >
                                <button className="modal-close" onClick={closeModal}>&times;</button>
                                <h2 style={{ marginBottom: '2rem' }}>
                                    {editIndex !== null ? 'Edit' : 'Add'} {modalType === 'area' ? 'Research Area' : 'Publication'}
                                </h2>

                                {modalType === 'area' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        <div style={{ display: 'flex', gap: '1rem' }}>
                                            <input
                                                placeholder="Icon"
                                                value={formData.icon || ''}
                                                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                                style={{ width: '80px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.7rem', color: 'white' }}
                                            />
                                            <input
                                                placeholder="Area Title"
                                                value={formData.title || ''}
                                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                                style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.7rem', color: 'white' }}
                                            />
                                        </div>
                                        <textarea
                                            placeholder="Short Description (for card preview)"
                                            value={formData.description || ''}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            style={{ height: '80px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.7rem', color: 'white', resize: 'vertical', fontFamily: 'inherit' }}
                                        />
                                        <textarea
                                            placeholder="Detailed Description (for modal view)"
                                            value={formData.longDescription || ''}
                                            onChange={(e) => setFormData({ ...formData, longDescription: e.target.value })}
                                            style={{ height: '120px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.7rem', color: 'white', resize: 'vertical', fontFamily: 'inherit' }}
                                        />
                                        <input
                                            placeholder="Tech Stack (comma-separated)"
                                            value={formData.tech || ''}
                                            onChange={(e) => setFormData({ ...formData, tech: e.target.value })}
                                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.7rem', color: 'white' }}
                                        />
                                        <input
                                            placeholder="Project Link"
                                            value={formData.link || ''}
                                            onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.7rem', color: 'white' }}
                                        />
                                        <input
                                            placeholder="GitHub Link"
                                            value={formData.github || ''}
                                            onChange={(e) => setFormData({ ...formData, github: e.target.value })}
                                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.7rem', color: 'white' }}
                                        />
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        <input
                                            placeholder="Paper Title"
                                            value={formData.title || ''}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.7rem', color: 'white' }}
                                        />
                                        <input
                                            placeholder="Authors"
                                            value={formData.authors || ''}
                                            onChange={(e) => setFormData({ ...formData, authors: e.target.value })}
                                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.7rem', color: 'white' }}
                                        />
                                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
                                            <input
                                                placeholder="Venue/Conference"
                                                value={formData.venue || ''}
                                                onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                                                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.7rem', color: 'white' }}
                                            />
                                            <input
                                                type="number"
                                                placeholder="Year"
                                                value={formData.year || ''}
                                                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                                                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.7rem', color: 'white' }}
                                            />
                                        </div>
                                        <textarea
                                            placeholder="Abstract/Summary"
                                            value={formData.abstract || ''}
                                            onChange={(e) => setFormData({ ...formData, abstract: e.target.value })}
                                            style={{ height: '100px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.7rem', color: 'white', resize: 'vertical', fontFamily: 'inherit' }}
                                        />
                                        <input
                                            placeholder="Paper Link (PDF/URL)"
                                            value={formData.link || ''}
                                            onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.7rem', color: 'white' }}
                                        />
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                                    <button onClick={handleModalSave} className="save-btn" style={{ flex: 1 }}>
                                        {editIndex !== null ? 'Update' : 'Add'}
                                    </button>
                                    <button onClick={closeModal} className="cancel-btn">Cancel</button>
                                </div>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>,
                document.getElementById('modal-root')
            )}
        </div>
    );
};

export default AdminResearch;
